import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { Box, Button, Dialog, Stack, Typography } from "@mui/material";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import LockClockIcon from "@mui/icons-material/LockClock";
import KeyIcon from "@mui/icons-material/Key";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import { authHeader, isReauthCancelled } from "../../authClient";
import { addPasskey } from "../../auth/passkeys";
import { COLORS } from "./constants";
import { ghostButtonSx, primaryButtonSx, dangerButtonSx } from "./styles";
import { ConfirmDialog } from "./ConfirmDialog";
import { RecoveryCodeView } from "../auth/RecoveryCodeView";

// ─────────────────────────────────────────────────────────────────────────────
// SecurityBanners — the account's security state, at the top of the Studio
//
//   • "Your recovery code was used. Was this you?" on every OTHER device of the
//     account, until answered. "This wasn't me" signs the recovery out, removes
//     the passkeys it added and hands this device a new recovery code.
//   • The 24-hour restriction, on a session that came from a recovery.
//   • No recovery code (e.g. one was used and never replaced) → make one.
//   • Older accounts without a passkey → offer one, once per sign-in.
//
// Polls the alerts every 60 s and whenever the tab regains focus. Answers
// need a passkey check; authClient opens that dialog when the server asks.
// ─────────────────────────────────────────────────────────────────────────────

const POLL_MS = 60_000;
const when = (d) => new Date(d).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function Banner({ tone = "warn", icon, title, children, actions }) {
  const t = tone === "danger"
    ? { bg: "rgba(240,104,108,0.10)", border: "rgba(240,104,108,0.45)", fg: COLORS.red }
    : tone === "info"
      ? { bg: "rgba(43,189,175,0.08)", border: "rgba(43,189,175,0.35)", fg: COLORS.cyan }
      : { bg: "rgba(244,183,64,0.08)", border: "rgba(244,183,64,0.40)", fg: COLORS.amber };
  return (
    <Box role="status" sx={{
      display: "flex", gap: 1.75, flexWrap: { xs: "wrap", md: "nowrap" }, alignItems: "flex-start",
      p: { xs: 1.75, sm: 2.25 }, borderRadius: "16px", bgcolor: t.bg, border: `1px solid ${t.border}`
    }}>
      <Box sx={{ color: t.fg, display: "flex", mt: "2px" }}>{icon}</Box>
      <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: COLORS.text }}>{title}</Typography>
        <Typography component="div" sx={{ mt: 0.4, fontSize: 13.5, lineHeight: 1.55, color: COLORS.textSoft }}>{children}</Typography>
      </Box>
      {actions && <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: "wrap", rowGap: 1 }}>{actions}</Stack>}
    </Box>
  );
}

function CodeDialog({ code, title, intro, onClose }) {
  return (
    <Dialog open={!!code} onClose={() => {}} disableEscapeKeyDown
      PaperProps={{ sx: { width: "100%", maxWidth: 480, m: 2, p: { xs: 3, sm: 3.5 }, bgcolor: COLORS.raised,
        backgroundImage: "none", color: COLORS.text, border: `1px solid ${COLORS.borderStrong}`, borderRadius: "22px" } }}
      sx={{ "& .MuiBackdrop-root": { bgcolor: "rgba(8,7,24,0.78)" } }}>
      {code && <RecoveryCodeView tone="dark" code={code} title={title} intro={intro} continueLabel="Done" onContinue={onClose} />}
    </Dialog>
  );
}

export function SecurityBanners({ showMessage, onAccountChanged }) {
  const [me, setMe] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [confirmNotMe, setConfirmNotMe] = useState(null);
  const [newCode, setNewCode] = useState(null); // { code, title, intro }
  const [promptOpen, setPromptOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const say = useCallback((text, kind = "info") => { if (showMessage) showMessage(text, kind); }, [showMessage]);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/me", { headers: authHeader(), skipReauth: true });
      setMe(data);
      if (data.showPasskeyPrompt) setPromptOpen(true);
    } catch { /* the dashboard keeps working without it */ }
  }, []);

  const loadAlerts = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/security/alerts", { headers: authHeader(), skipReauth: true });
      setAlerts(Array.isArray(data.alerts) ? data.alerts : []);
    } catch { /* keep the last list */ }
  }, []);

  useEffect(() => {
    loadMe();
    loadAlerts();
    const iv = setInterval(() => { loadAlerts(); setNow(Date.now()); }, POLL_MS);
    const onFocus = () => { loadAlerts(); loadMe(); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("fennira:restricted", loadMe);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("fennira:restricted", loadMe);
    };
  }, [loadMe, loadAlerts]);

  const answer = async (alert, action) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`/api/security/alerts/${alert.id}/${action}`, {}, { headers: authHeader() });
      setAlerts((list) => list.filter((a) => a.id !== alert.id));
      if (action === "not-me") {
        setNewCode({
          code: data.recoveryCode,
          title: "Recovery cancelled",
          intro: `We signed out ${data.revokedSessions} device${data.revokedSessions === 1 ? "" : "s"} and removed ` +
                 `${data.removedPasskeys} passkey${data.removedPasskeys === 1 ? "" : "s"} added with your old code. ` +
                 "That code no longer works. Here is your new one, shown only now."
        });
        onAccountChanged?.();
      } else {
        say("Thanks. The alert is closed.", "success");
      }
    } catch (err) {
      if (!isReauthCancelled(err)) say(err?.response?.data?.message || "That didn't work. Please try again.", "error");
    } finally {
      setBusy(false);
      setConfirmNotMe(null);
    }
  };

  const addFirstPasskey = async () => {
    setBusy(true);
    try {
      const data = await addPasskey();
      setPromptOpen(false);
      if (data.recoveryCode) {
        setNewCode({ code: data.recoveryCode, title: "Save your recovery code",
          intro: "Your account now has a passkey. If you ever lose your devices, this code gets you back in. It's shown only now." });
      } else {
        say("Passkey added.", "success");
      }
      loadMe();
    } catch (err) {
      if (!err.cancelled && !isReauthCancelled(err)) say(err.message, "error");
    } finally { setBusy(false); }
  };

  const snooze = async () => {
    setPromptOpen(false);
    try { await axios.post("/api/me/passkey-prompt/snooze", {}, { headers: authHeader(), skipReauth: true }); } catch { /* shown again next time */ }
  };

  const restrictedUntil = me?.session?.restrictedUntil && new Date(me.session.restrictedUntil).getTime() > now
    ? me.session.restrictedUntil : null;
  const noRecoveryCode = me && me.passkeyCount > 0 && !me.hasRecoveryCode && !me.session?.mustRegisterPasskey;

  return (
    <>
      {(alerts.length > 0 || restrictedUntil || noRecoveryCode) && (
        <Stack spacing={1.25}>
          {alerts.map((a) => (
            <Banner key={a.id} tone="danger" icon={<GppMaybeIcon />}
              title="Your recovery code was used. Was this you?"
              actions={<>
                <Button disabled={busy} onClick={() => answer(a, "confirm")} sx={{ ...ghostButtonSx, height: 38 }}>It was me</Button>
                <Button disabled={busy} onClick={() => setConfirmNotMe(a)} sx={{ ...dangerButtonSx, height: 38 }}>This wasn't me</Button>
              </>}>
              Someone signed in to your account with your recovery code on {when(a.createdAt)}
              {a.deviceName ? ` (${a.deviceName})` : ""}. If it wasn't you, cancel it now: the other person is signed
              out and loses access.
            </Banner>
          ))}
          {restrictedUntil && (
            <Banner icon={<LockClockIcon />} title={`Limited until ${when(restrictedUntil)}`}>
              You signed in with a recovery code. To protect the account, stream keys, destinations and some security
              settings stay locked for 24 hours. Everything else works as usual.
            </Banner>
          )}
          {noRecoveryCode && !restrictedUntil && (
            <Banner tone="info" icon={<KeyIcon />} title="You don't have a recovery code"
              actions={<Button component={RouterLink} to="/settings/security" sx={{ ...primaryButtonSx, height: 38 }}>Create one</Button>}>
              Without it, losing all your passkeys would lock you out for good.
            </Banner>
          )}
        </Stack>
      )}

      <ConfirmDialog
        open={!!confirmNotMe}
        onClose={() => !busy && setConfirmNotMe(null)}
        onConfirm={() => confirmNotMe && answer(confirmNotMe, "not-me")}
        icon={<GppMaybeIcon />}
        title="Cancel this recovery?"
        body="Fennira signs out the device that used your recovery code, removes any passkey it added, and gives you a new recovery code. You'll confirm with your passkey first."
        cancelLabel="Back"
        confirmLabel="Yes, it wasn't me"
      />

      <Dialog open={promptOpen} onClose={snooze}
        PaperProps={{ sx: { width: "100%", maxWidth: 440, m: 2, p: { xs: 3, sm: 3.5 }, bgcolor: COLORS.raised,
          backgroundImage: "none", color: COLORS.text, border: `1px solid ${COLORS.borderStrong}`, borderRadius: "22px" } }}
        sx={{ "& .MuiBackdrop-root": { bgcolor: "rgba(8,7,24,0.72)" } }}>
        <Stack direction="row" spacing={2}>
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", flexShrink: 0, bgcolor: "rgba(43,189,175,0.14)",
            color: COLORS.cyan, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FingerprintIcon />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: COLORS.fontDisplay, fontWeight: 700, fontSize: 20 }}>Add a passkey</Typography>
            <Typography sx={{ mt: 1, color: COLORS.textSoft, fontSize: 14, lineHeight: 1.55 }}>
              Sign in with your fingerprint, face or screen lock instead of a password. It can't be phished or
              guessed, and you'll also get a recovery code in case you lose your devices.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.25} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button onClick={snooze} disabled={busy} sx={ghostButtonSx}>Not now</Button>
          <Button onClick={addFirstPasskey} disabled={busy} startIcon={<FingerprintIcon />} sx={primaryButtonSx}>Add a passkey</Button>
        </Stack>
      </Dialog>

      <CodeDialog {...(newCode || {})} onClose={() => setNewCode(null)} />
    </>
  );
}
